import { ApiError } from "@/lib/api";
import { parseFilenameFromHeader } from "@/lib/download";
import { consumeSse } from "@/lib/sse";
import type { PaaChangeVerdict, PaaDriver, PaaExtraction } from "./types";

// Reuse the objection flow's saved-property DB + Google Maps wiring verbatim —
// both flows share the same PropertyInfo table (keyed on normalized address) —
// and the shared stats-session lifecycle so PAA runs show on the dashboard.
export {
  getPropertyInfo,
  savePropertyInfo,
  startSession,
  endSession,
  type PropertyInfoDto,
  type SessionStatus,
} from "@/features/upload/api";

// POST /api/paa/extract — multipart file -> PaaExtraction (non-streaming).
export async function extractPaaPdfStreaming(
  file: File,
  opts: {
    onProgress: (msg: string) => void;
    signal?: AbortSignal;
    // Stats session id, so the AI fallback parser's cost shows on the dashboard.
    sessionId?: string | null;
  },
): Promise<PaaExtraction> {
  const form = new FormData();
  form.append("file", file);
  if (opts.sessionId) form.append("sessionId", opts.sessionId);

  const res = await fetch("/api/paa/extract-stream", {
    method: "POST",
    credentials: "same-origin",
    body: form,
    signal: opts.signal,
  });

  return new Promise<PaaExtraction>((resolve, reject) => {
    // .catch handles the case where the stream read throws (e.g. the user
    // aborts mid-stream) before consumeSse can surface onError, so the
    // awaiting caller doesn't hang.
    consumeSse<PaaExtraction>(res, {
      onProgress: opts.onProgress,
      onResult: resolve,
      onError: (msg) => reject(new Error(msg)),
    }).catch(reject);
  });
}

// One `row` SSE event from annotate-stream: the note for a single sheet, the
// moment it lands (sheets finish out of order).
export interface PaaRowNotePatch {
  id: string;
  changeNote: string | null;
  /** The note exactly as the engine wrote it. Held untouched while the user
   *  edits changeNote; the pair is recorded as a correction at generate time. */
  aiChangeNote: string | null;
  oldSheetMissing: boolean;
  /** Approved-set sheet (base id) actually used as the baseline; null if none. */
  comparedTo: string | null;
  /** The router's verdict and its reasoning, plus the two flags that need a
   *  human: a revision the architect forgot to bump, and a sheet that changed
   *  without anything readable differing. See PaaRow for why these matter. */
  verdict: PaaChangeVerdict | null;
  verdictExplanation: string | null;
  revisionLooksStale: boolean;
  needsReview: boolean;
  reviewReason: string | null;
  /** The evidence behind the note — see PaaRow.readSummary. */
  readSummary: string | null;
  unexplainedAreas: number;
  readOnOneSide: number;
  blindSpot: string | null;
}

// One sheet detected in the approved set (baseline dropdown option).
export interface PaaOldSheet {
  sheet: string;
  page: number;
}

// The `context` SSE event, emitted once both sets are indexed: the approved
// set's sheets (baseline dropdown options) plus the id the server parked the
// indexed sets under. Send that id back to rewrite notes (recomposePaa) or
// re-compare a single row without re-uploading either PDF. It expires — every
// caller must keep both Files and be able to fall back to a full run.
export interface PaaCompareContextInfo {
  compareId: string;
  oldSheets: PaaOldSheet[];
}

// Thrown by recomposePaa when the server no longer holds the indexed sets. The
// caller's move is a full re-comparison, not an error toast.
export class PaaCompareExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PaaCompareExpiredError";
  }
}

// POST /api/paa/annotate-stream — the "what changed" comparison, run from the
// review page against the user's CURRENT rows (so corrected sheet ids improve
// matching). Streams progress into onProgress and one onRow per annotated
// sheet; the final annotated extraction resolves the promise.
export async function annotatePaaStreaming(
  newFile: File,
  oldFile: File,
  data: PaaExtraction,
  opts: {
    /** A live compare session from a previous run. When the server still holds
     *  it, neither PDF is uploaded and the indexed sets are reused — this is
     *  what makes a per-row re-compare instant. Silently ignored (full run,
     *  files uploaded) once it has expired. */
    compareId?: string | null;
    onProgress: (msg: string) => void;
    onRow: (patch: PaaRowNotePatch) => void;
    /** Typed per-sheet counter — fires (done, total) as each sheet is compared,
     *  driving the overlay's progress bar (no scraping the prose messages). */
    onSheetProgress?: (done: number, total: number) => void;
    /** Fires once both sets are indexed — carries the approved set's sheet
     *  list (baseline dropdown options). */
    onContext?: (ctx: PaaCompareContextInfo) => void;
    /** Fires when the run has derived (or extended) the amendment's drivers.
     *  Store them on the extraction so the next annotate call reuses the same
     *  ids and phrasing. */
    onDrivers?: (drivers: PaaDriver[]) => void;
    /** The amendment in a paragraph, once composition finishes. */
    onGist?: (gist: string) => void;
    /** Restrict the run to these row ids (per-row re-compare). */
    rowIds?: string[];
    signal?: AbortSignal;
    sessionId?: string | null;
  },
): Promise<PaaExtraction> {
  const form = new FormData();
  // With a live compare session the server needs neither file — skipping the
  // two ~100MB parts is most of why a cached re-compare feels instant. The
  // server falls back to "files required" if the id turns out to be expired,
  // so only omit them when we're actually sending an id.
  if (!opts.compareId) {
    form.append("newFile", newFile);
    form.append("oldFile", oldFile);
  } else {
    form.append("compareId", opts.compareId);
  }
  form.append("data", JSON.stringify(data));
  if (opts.rowIds?.length) form.append("rowIds", JSON.stringify(opts.rowIds));
  if (opts.sessionId) form.append("sessionId", opts.sessionId);

  const res = await fetch("/api/paa/annotate-stream", {
    method: "POST",
    credentials: "same-origin",
    body: form,
    signal: opts.signal,
  });

  return new Promise<PaaExtraction>((resolve, reject) => {
    consumeSse<PaaExtraction>(res, {
      onProgress: opts.onProgress,
      onResult: resolve,
      onError: (msg) => reject(new Error(msg)),
      onEvent: (event, payload) => {
        if (event === "row") opts.onRow(payload as PaaRowNotePatch);
        else if (event === "context") opts.onContext?.(payload as PaaCompareContextInfo);
        else if (event === "drivers") opts.onDrivers?.(payload as PaaDriver[]);
        else if (event === "gist") {
          const g = (payload as { gist?: string | null })?.gist;
          if (g) opts.onGist?.(g);
        }
        else if (event === "compareProgress") {
          const p = payload as { done: number; total: number };
          opts.onSheetProgress?.(p.done, p.total);
        }
      },
    }).catch(reject);
  });
}

// GET /api/paa/sheet-image — one sheet of a live comparison, rendered as a PNG.
// Used as an <img src>, so this returns the URL rather than fetching: the
// browser handles loading, caching and cancellation, and a big drawing streams
// in instead of being buffered in JS.
export function sheetImageUrl(
  compareId: string,
  sheet: string,
  side: "old" | "new",
): string {
  const params = new URLSearchParams({ compareId, sheet, side });
  return `/api/paa/sheet-image?${params}`;
}

// Where two versions of a sheet differ. Coordinates are FRACTIONS of the page
// with the origin top-left, so they map onto the rendered PNG at any DPI.
export interface PaaDiffBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

// "Text" changes are exact to the line and can quote themselves. "Graphics"
// regions come from comparing the rendered images — approximate, and the only
// way to catch a redrawn wall whose labels never changed.
/** How a change was found. Text is exact to the line; Graphics is an
 *  approximate region from comparing the rendered drawings. */
export type PaaSheetChangeKind = "Text" | "Graphics";

export interface PaaSheetChange {
  index: number;
  old: PaaDiffBox | null;
  new: PaaDiffBox | null;
  kind: PaaSheetChangeKind;
  oldText?: string | null;
  newText?: string | null;
}

export interface PaaSheetDiff {
  sheet: string;
  baseline: string;
  changes: PaaSheetChange[];
  /** The two renders couldn't be trusted to line up (usually a re-plot), so the
   *  graphics pass was skipped. Text changes are still exact — the UI must say
   *  this rather than imply the sheet is unchanged. */
  graphicsComparisonSkipped: boolean;
  graphicsSkipReason?: string | null;
  /** Found but not returned, because stepping through hundreds helps nobody. */
  changesOmitted: number;
  textChangeCount: number;
  graphicsChangeCount: number;
}

// GET /api/paa/sheet-diff — change markers for one sheet.
//
// Called twice on open: `graphics: false` first, which is instant (the indexed
// tokens are already in the session) so markers appear immediately, then again
// with the graphics pass, which has to render both pages. Throws
// PaaCompareExpiredError on 410 so the viewer shows the "run it again" state.
export async function fetchSheetDiff(
  compareId: string,
  sheet: string,
  baseline: string,
  opts: { graphics: boolean; signal?: AbortSignal },
): Promise<PaaSheetDiff> {
  const params = new URLSearchParams({
    compareId,
    sheet,
    baseline,
    graphics: String(opts.graphics),
  });
  const res = await fetch(`/api/paa/sheet-diff?${params}`, {
    credentials: "same-origin",
    signal: opts.signal,
  });
  if (res.status === 410) {
    throw new PaaCompareExpiredError("The comparison is no longer held on the server.");
  }
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* swallow */ }
    throw new ApiError(res.status, body);
  }
  return (await res.json()) as PaaSheetDiff;
}

// POST /api/paa/recompose — rewrite the change notes against the driver list
// the operator settled on. The indexed plan sets and every sheet's recorded
// facts already live under `compareId`, so this is the composition pass alone:
// one AI call, no upload. That's what lets "edit a driver -> see the notes
// change" be an interactive loop.
//
// `skipRowIds` are notes the user typed by hand — never overwritten.
// Throws PaaCompareExpiredError when the session is gone; the caller should
// offer a full re-comparison rather than reporting a failure.
export async function recomposePaa(
  compareId: string,
  data: PaaExtraction,
  opts: {
    onProgress: (msg: string) => void;
    onRow: (patch: PaaRowNotePatch) => void;
    onDrivers?: (drivers: PaaDriver[]) => void;
    /** The amendment in a paragraph, once composition finishes. */
    onGist?: (gist: string) => void;
    skipRowIds?: string[];
    signal?: AbortSignal;
    sessionId?: string | null;
  },
): Promise<PaaExtraction> {
  const res = await fetch("/api/paa/recompose", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      compareId,
      data,
      skipRowIds: opts.skipRowIds ?? [],
      sessionId: opts.sessionId ?? null,
    }),
    signal: opts.signal,
  });

  return new Promise<PaaExtraction>((resolve, reject) => {
    consumeSse<PaaExtraction>(res, {
      onProgress: opts.onProgress,
      onResult: resolve,
      onError: (msg) => reject(new Error(msg)),
      onEvent: (event, payload) => {
        if (event === "row") opts.onRow(payload as PaaRowNotePatch);
        else if (event === "drivers") opts.onDrivers?.(payload as PaaDriver[]);
        else if (event === "gist") {
          const g = (payload as { gist?: string | null })?.gist;
          if (g) opts.onGist?.(g);
        }
        else if (event === "expired") reject(new PaaCompareExpiredError(String(payload)));
      },
    }).catch(reject);
  });
}

// DELETE /api/paa/compare — "I'm done with this comparison, stop holding it."
//
// FIRE AND FORGET. An indexed pair of plan sets is hundreds of megabytes on the
// server, and an abandoned review otherwise pins it for a full 30 minutes — which
// on a 2 vCPU / 4 GB box is the difference between the next operator's comparison
// running and the container being killed. Nothing depends on the result: if the
// call is lost the session just expires on its own timer.
//
// `keepalive` so it survives the navigation that usually triggers it.
export function releasePaaCompare(compareId: string | null | undefined): void {
  if (!compareId) return;
  void fetch(`/api/paa/compare?compareId=${encodeURIComponent(compareId)}`, {
    method: "DELETE",
    credentials: "same-origin",
    keepalive: true,
  }).catch(() => {
    /* the idle timer is the backstop */
  });
}

// POST /api/paa/generate-pdf — JSON body PaaExtraction -> PDF blob + filename.
// sessionId links the archived document to its stats session; documentId
// targets the draft created at extraction time so generate finalizes it
// instead of inserting a duplicate row.
//
// compareId is passed so the server can DROP the retained comparison: the form
// is out, so two indexed plan sets are no longer worth hundreds of megabytes of
// the container's memory. Nothing breaks if it is missing or already gone — a
// released session is indistinguishable from an expired one, and the client
// still holds both Files.
export async function generatePaaPdf(
  data: PaaExtraction,
  signal?: AbortSignal,
  sessionId?: string | null,
  documentId?: string | null,
  compareId?: string | null,
): Promise<{ blob: Blob; filename: string }> {
  const params = new URLSearchParams();
  if (sessionId) params.set("sessionId", sessionId);
  if (documentId) params.set("documentId", documentId);
  if (compareId) params.set("compareId", compareId);
  const qs = params.size ? `?${params}` : "";
  const res = await fetch(`/api/paa/generate-pdf${qs}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/pdf" },
    body: JSON.stringify(data),
    signal,
  });

  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* swallow */ }
    throw new ApiError(res.status, body);
  }
  const blob = await res.blob();
  const filename =
    parseFilenameFromHeader(res.headers.get("content-disposition")) ?? "ai1-paa.pdf";
  return { blob, filename };
}

// POST /api/paa/note-edit — a person finished editing one sheet's note.
//
// FIRE AND FORGET, ON PURPOSE. Recording only the FILED text loses the work that
// matters most: an operator who rewrites fifteen notes and then closes the tab,
// or whose download fails, or who comes back tomorrow, leaves no trace at all.
// So the edit is captured when the field is committed — never per keystroke,
// because mid-edit states are noise and thousands of them would bury the rows
// that mean something.
//
// Failures are swallowed. A learning record must never interrupt a review, and
// the server dedups against the sheet's last recorded version, so calling this
// on every blur is safe.
export function recordNoteEdit(
  data: PaaExtraction,
  rowId: string,
  sessionId: string | null,
): void {
  void fetch("/api/paa/note-edit", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data, rowId, sessionId }),
  }).catch(() => {});
}

// Report that a sheet was read wrong, or that a change was missed.
//
// NOT fire-and-forget, unlike recordNoteEdit. She typed a sentence explaining
// what we got wrong; swallowing a failure would teach her that reporting does
// nothing, and one silent loss costs more than the bug it hides.
//
// `compareId` decides whether the drawings survive. The server captures both
// sheets at report time because the comparison holding them expires and nothing
// else keeps the plan sets — so a report filed after an expiry is still stored,
// just without the evidence. The resolved `captured` says which happened.
export async function reportSheetProblem(
  data: PaaExtraction,
  rowId: string,
  sessionId: string | null,
  compareId: string | null,
  kind: "Misdiagnosed" | "Missed",
  message: string,
  /** Set when revising something already sent — edits it instead of filing a
   *  near-duplicate. The drawings captured the first time are kept as they were. */
  reportId: string | null = null,
): Promise<{ id: string; captured: boolean | null }> {
  const response = await fetch("/api/paa/report", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data, rowId, sessionId, compareId, kind, message, reportId }),
  });

  if (!response.ok) {
    const problem = await response.json().catch(() => null);
    throw new Error(problem?.error ?? "The report could not be saved.");
  }

  return response.json();
}
